import { takeEvery, call, select, put, all } from 'redux-saga/effects';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';

import { waitForModal } from 'components/Modal/Modal.saga';
import { trackInstructorCreated, trackInstructorUpdated, trackInstructorDeleted } from 'helpers/events';

import { instructorsData, instructorData, msInventoryVenues } from 'store/saga/entities/bound';
import { IA_KEY } from 'helpers/ia-logic';
import { makePath } from 'helpers/link-generation';
import { selectActiveVenueId, selectRouteParams, selectIaLogic } from 'selectors/general';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { ACTIVE_VENUE_CHANGED, waitForAppInit } from 'features/App/App.saga';
import { reportError } from 'helpers/error-logging';
import { instructorPhotoExceedsMaxSize, isPermissionDenied, isPhotoTooLarge } from 'helpers/proxy-api-errors';

import { messages } from './Instructors.messages';

import { setSelectedInstructorId } from './Instructors.reducers';
import { selectSelectedInstructorId } from './Instructors.selectors';

export const FETCH_INSTRUCTOR = 'FETCH_INSTRUCTOR';
export const FETCH_INSTRUCTORS = 'FETCH_INSTRUCTORS';
export const SAVE_INSTRUCTOR = 'SAVE_INSTRUCTOR';
export const DELETE_INSTRUCTOR = 'DELETE_INSTRUCTOR';
export const FETCH_INSTRUCTOR_VENUES = 'FETCH_INSTRUCTOR_VENUES';
export const SET_SELECTED_INSTRUCTOR = 'SET_SELECTED_INSTRUCTOR';
export const CANCEL_INSTRUCTOR_FORM = 'CANCEL_INSTRUCTOR_FORM';

export const fetchInstructor = ({ instructorId, nextVenueId }) => ({
  type: FETCH_INSTRUCTOR,
  instructorId,
  nextVenueId,
});
export const fetchInstructors = () => ({ type: FETCH_INSTRUCTORS });
export const saveInstructor = (id, instructor) => ({ type: SAVE_INSTRUCTOR, id, instructor });
export const deleteInstructor = id => ({ type: DELETE_INSTRUCTOR, id });
export const fetchInstructorVenues = () => ({ type: FETCH_INSTRUCTOR_VENUES });
export const setSelectedInstructor = ({ instructorId, shouldScrollToItem, focus, updatePath }) => ({
  type: SET_SELECTED_INSTRUCTOR,
  instructorId,
  shouldScrollToItem,
  focus,
  updatePath,
});
export const cancelInstructorForm = () => ({ type: CANCEL_INSTRUCTOR_FORM });

// TODO: don't make MSI calls for non-MSI venues
// TODO: once the BE correctly returns a 403, unwind this so 401s force login
const isVenueNotSupported = ({ response }) => response && (response.status === 401 || isPermissionDenied({ response }));

export function* doFetchInstructor(client, action) {
  try {
    const { instructorId, nextVenueId } = action;
    const activeVenueId = yield select(selectActiveVenueId);
    const venueId = nextVenueId || activeVenueId;

    yield call(fetchEntityIfNeeded, instructorData, [client, venueId, instructorId]);
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doFetchInstructors(client, action = {}) {
  try {
    const { nextVenueId } = action;
    const activeVenueId = yield select(selectActiveVenueId);
    const venueId = nextVenueId || activeVenueId;

    const instructors = yield call(fetchEntity, instructorsData, [client, venueId]);
    return instructors;
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doSetSelectedInstructor(client, action) {
  const { shouldScrollToItem, instructorId, nextVenueId, focus, updatePath = true } = action;
  const activeVenueId = yield select(selectActiveVenueId);
  const venueId = nextVenueId || activeVenueId;

  yield put(setSelectedInstructorId({ instructorId, shouldScrollToItem }));
  yield put(fetchInstructor({ instructorId, nextVenueId: venueId }));
  if (updatePath) {
    yield call(client.navigate, makePath.instructors({ venueId, focus }));
  }
}

export function* doSaveInstructor(client, action) {
  try {
    const { instructor, id } = action;
    if (instructorPhotoExceedsMaxSize(instructor.picture)) {
      yield put(createErrorBanner(messages.error.photoTooLarge));
      return;
    }

    const venueId = yield select(selectActiveVenueId);
    const { id: instructorId } = yield call(fetchEntity, instructorData, [client, venueId, id, instructor]);

    yield all([
      call(doFetchInstructors, client),
      call(doSetSelectedInstructor, client, { instructorId, shouldScrollToItem: true, updatePath: false }),
    ]);

    // Track the instructor operation
    if (id) {
      // Update operation
      yield call(trackInstructorUpdated, {
        instructorId,
        venueId,
        instructorName: instructor.name,
        email: instructor.email,
        changes: Object.keys(instructor),
      });
      yield put(createInfoBanner(messages.success.instructorEdited));
    } else {
      // Create operation
      yield call(trackInstructorCreated, {
        instructorId,
        venueId,
        instructorName: instructor.name,
        email: instructor.email,
      });
      yield put(createInfoBanner(messages.success.instructorAdded));
    }

    yield call(client.navigate, makePath.instructors({ venueId, focus: 'details' }));
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.error.notSupported));
    } else if (isPhotoTooLarge(error)) {
      yield put(createErrorBanner(messages.error.photoTooLarge));
    } else {
      yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doDeleteInstructor(client, action) {
  try {
    const { id } = action;
    const venueId = yield select(selectActiveVenueId);
    const confirmed = yield call(waitForModal, client, 'DeleteInstructorModal');

    if (confirmed) {
      // Get instructor data before deletion for tracking
      const instructor = yield call(fetchEntityIfNeeded, instructorData, [client, venueId, id]);

      // Delete the instructor via fetchEntity pattern
      yield call(fetchEntity, instructorData, [client, venueId, id, { removed: true }]);

      // Track the instructor deletion
      yield call(trackInstructorDeleted, {
        instructorId: id,
        venueId,
        instructorName: instructor?.name,
        email: instructor?.email,
      });

      // Fetch updated instructor list
      const instructors = yield call(doFetchInstructors, client);

      // Select the first instructor if any exist, otherwise clear selection
      if (instructors && instructors.length > 0) {
        yield call(doSetSelectedInstructor, client, {
          instructorId: instructors[0].id,
          shouldScrollToItem: true,
          updatePath: false,
        });
      }

      yield all([
        put(createInfoBanner(messages.success.instructorDeleted)),
        call(client.navigate, makePath.instructors({ venueId, focus: 'list' })),
      ]);
    }
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doFetchInstructorVenues(client, action = {}) {
  try {
    const { nextVenueId } = action;
    const activeVenueId = yield select(selectActiveVenueId);
    const venueId = nextVenueId || activeVenueId;

    yield call(fetchEntityIfNeeded, msInventoryVenues, [client, venueId]);
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doCancelInstructorForm(client) {
  const venueId = yield select(selectActiveVenueId);
  yield call(client.navigate, makePath.instructors({ venueId, focus: 'list' }));
}

export function* handleRouteEnter(client) {
  yield call(waitForAppInit);
  const { featureIsSupported } = yield select(selectIaLogic, IA_KEY.MANAGE_INSTRUCTORS);

  if (featureIsSupported) {
    const [instructors] = yield all([call(doFetchInstructors, client), call(doFetchInstructorVenues, client)]);
    const instructorId = yield select(selectSelectedInstructorId);
    if (!instructorId && instructors?.length) {
      yield call(doSetSelectedInstructor, client, { instructorId: instructors[0].id });
    }
  }
}

export function* handleFormRouteEnter(client) {
  yield call(waitForAppInit);
  const { featureIsSupported } = yield select(selectIaLogic, IA_KEY.MANAGE_INSTRUCTORS);

  if (featureIsSupported) {
    yield all([call(doFetchInstructors, client), call(doFetchInstructorVenues, client)]);
  }
}

export function* handleEditRouteEnter(client) {
  const { id } = yield select(selectRouteParams);
  yield call(doFetchInstructor, client, { instructorId: id });
}

function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  const { featureIsSupported } = yield select(selectIaLogic, IA_KEY.MANAGE_INSTRUCTORS, venueId);

  // Replicating some handleRouteEnter logic here because we don't want to use locationSagas
  // on the route because changing selected instructor causes a lot of unnecessary API calls.
  if (featureIsSupported) {
    const [instructors] = yield all([
      call(doFetchInstructors, client, { nextVenueId: venueId }),
      call(doFetchInstructorVenues, client, { nextVenueId: venueId }),
    ]);
    if (instructors.length) {
      yield call(doSetSelectedInstructor, client, {
        instructorId: instructors[0].id,
        nextVenueId: venueId,
        updatePath: false,
      });
    }
  }

  const newPath = makePath.instructors({ venueId });
  yield all([put(setSelectedInstructorId({ instructorId: null })), call(client.navigate, newPath)]);
}

export function* watchInstructorsActions(client) {
  yield takeEvery(FETCH_INSTRUCTOR, doFetchInstructor, client);
  yield takeEvery(FETCH_INSTRUCTORS, doFetchInstructors, client);
  yield takeEvery(SAVE_INSTRUCTOR, doSaveInstructor, client);
  yield takeEvery(DELETE_INSTRUCTOR, doDeleteInstructor, client);
  yield takeEvery(FETCH_INSTRUCTOR_VENUES, doFetchInstructorVenues, client);
  yield takeEvery(SET_SELECTED_INSTRUCTOR, doSetSelectedInstructor, client);
  yield takeEvery(CANCEL_INSTRUCTOR_FORM, doCancelInstructorForm, client);
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, client);
}
